import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import CapsulePriceForm from './CapsulePriceForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  CapsulePricePayload,
  useGetCapsulePriceLazyQuery,
  useUpdateCapsulePriceMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  EDIT_CAPSULE_PRICE_TEXT,
  CAPSULE_PRICE_IS_UPDATED_TEXT,
  capsulePriceInitialValue,
} from 'constants/index';
import { CapsulePriceFormType, ParamType } from 'interfaces';
import { capsulePriceSchema } from 'validationSchema';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const methods = useForm<CapsulePriceFormType>({
    defaultValues: capsulePriceInitialValue,
    resolver: yupResolver(capsulePriceSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [updateCapsulePrice, { loading: updateLoading }] = useUpdateCapsulePriceMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateCapsulePrice } = data;
      const { response } = updateCapsulePrice || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getCapsulePrice, { loading: getLoading }] = useGetCapsulePriceLazyQuery({
    onCompleted: (data) => {
      const { getCapsulePrice } = data || {};
      const { response, capsulePrice } = getCapsulePrice || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(capsulePrice as CapsulePricePayload['capsulePrice']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onCapsulePriceSubmit: SubmitHandler<CapsulePriceFormType> = async (data) => {
    const { capacity, density, price, productSize, supplier, capsuleType } = data || {};

    const { value: capsuleTypeId } = capsuleType || {};
    const { value: supplierId } = supplier || {};
    const { value: productSizeId } = productSize || {};
    const { value: densityId } = density || {};

    if (id) {
      await updateCapsulePrice({
        variables: {
          updateCapsulePriceInput: {
            id: id || '',

            price: Number(price),
            capacity: Number(capacity),

            densityId,
            supplierId,
            productSizeId,
            capsuleTypeId,
          },
        },
      });
    }
  };

  const fetchCapsulePrice = useCallback(async () => {
    id &&
      (await getCapsulePrice({
        variables: {
          getCapsulePriceInput: {
            id,
          },
        },
      }));
  }, [id, getCapsulePrice]);

  const setValues = (params: CapsulePricePayload['capsulePrice']) => {
    const { capacity, density, price, productSize, supplier, capsuleType } = params || {};

    const { id: densityId, name: densityName } = density || {};
    const { id: supplierId, name: supplierName } = supplier || {};
    const { id: productSizeId, name: productSizeName } = productSize || {};
    const { id: capsuleTypeId, name: capsuleTypeName } = capsuleType || {};

    setValue('capacity', `${capacity || ''}`);
    setValue('price', `${price || ''}`);

    setValue('density', { value: densityId || '', name: densityName || '' });
    setValue('supplier', { value: supplierId || '', name: supplierName || '' });
    setValue('productSize', { value: productSizeId || '', name: productSizeName || '' });
    setValue('capsuleType', { value: capsuleTypeId || '', name: capsuleTypeName || '' });
  };

  useEffect(() => {
    id && fetchCapsulePrice();
  }, [id, fetchCapsulePrice]);

  const loading = updateLoading || getLoading;

  return (
    <Fragment>
      <Box sx={flexCenterBetween}>
        <Typography variant="h5">{EDIT_CAPSULE_PRICE_TEXT}</Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item lg={6} md={12}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onCapsulePriceSubmit)}>
              <CapsulePriceForm loading={loading} isEdit />
            </form>
          </FormProvider>
        </Grid>
      </Grid>

      <BackdropLoader open={loading} text={CAPSULE_PRICE_IS_UPDATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
